import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, paragraphRegular } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Form = styled.form `
  --tooltipFgColour: ${brand.global.white};
  --tooltipBgColour: ${brand.global.black};
  --borderColour: ${brand.global.black};
  --infoBgColour: ${brand.grey.grey80};
  --textColour: ${brand.global.black};

  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${fluid(32, 40)} 0;
`;
const Section = styled.fieldset `
  display: flex;
  flex-direction: column;
  gap: ${fluid(20, 24)};

  &[data-visible='false'] {
    display: none;
  }
`;
const Title = styled.legend `
  ${fonts.gotham.bold};

  display: block;
  font-size: ${fluid(18, 20)};
  margin: 0 0 30px;
  padding: 0 0 8px;
  color: var(--textColour);
  border-bottom: 1px solid var(--borderColour);
  width: 100%;

  @media ${from(Device.Tablet)} {
    margin-bottom: ${fluid(20, 24)};
  }
`;
const Content = styled.p `
  ${paragraphRegular}
`;
const Row = styled.div `
  --count: 1;

  display: grid;
  grid-template-columns: repeat(var(--count), 1fr);
  gap: ${fluid(20, 24)};

  @media ${from(Device.Tablet)} {
    --count: 2;

    &[data-layout='full'] {
      --count: 1;
    }
  }
`;
const Field = styled.div `
  display: flex;
  flex-direction: column;

  @media ${from(Device.Tablet)} {
    &[data-size='wide'] {
      grid-column: span 2;
    }

    &[data-size='full'] {
      grid-column: span 2;
    }
  }

  @media ${from(Device.Desktop)} {
    &[data-size='full'] {
      grid-column: span 4;
    }
  }
`;
export const CommonLabelStyles = css `
  ${fonts.gotham.bold};

  font-size: ${fluid(14, 16)};
  line-height: ${fluid(26, 26)};
  display: block;
  margin: 0 0 ${fluid(12, 16)};
  padding: 0 24px;
  color: var(--textColour);

  &.required,
  &[data-required='true'] {
    &::after {
      content: ' *';
      display: inline;
    }
  }

  &[data-hide='true'] {
    ${srOnly}
  }
`;
const Label = styled.label `
  ${CommonLabelStyles};
`;
const Description = styled.div `
  ${paragraphRegular};
`;
export const CommonErrorStyles = css `
  ${paragraphRegular};

  ${fonts.gotham.medium};
  font-size: 14px;
  line-height: 22px;
  display: block;
  color: ${brand.validation.invalid};
  margin: 8px 0 0;

  > span {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: ${fluid(16, 20)};
    height: ${fluid(16, 20)};
    border-radius: 50%;
    background-color: ${brand.validation.invalid};
    color: ${brand.global.white};
  }
`;
const Error = styled.span `
  ${CommonErrorStyles};
`;
const Divider = styled.hr `
  display: block;
  border: 0;
  border-bottom: 1px solid var(--borderColour);
  margin: 0;
  padding: 0;
`;
const SubmitWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;

  ${ButtonStyles.Container} {
    width: auto;
  }

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    align-items: center;
    gap: ${fluid(20, 24)};

    ${ButtonStyles.Container} {
      margin-left: auto;
    }
  }
`;
const Loader = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  background: ${rgba(brand.global.black, 0.2)};
  color: ${brand.global.white};
  flex-direction: column;
  height: 100%;
  place-content: center;
  place-items: center;
  text-align: center;
  width: 100%;
`;
const LoaderText = styled.span `
  ${paragraphRegular};
  ${fonts.gotham.bold};

  display: block;
  margin: 1rem 0 0;
`;
const ErrorList = styled.ul `
  display: flex;
  color: ${brand.validation.invalid};
  flex-direction: column;
  margin: 0;
`;
const ErrorListItem = styled.li `
  ${CommonErrorStyles};

  margin: 0;

  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;
const ConfirmationMessage = styled.div `
  ${contentStyles};

  p:last-child {
    margin-bottom: 0;
  }
`;
const Info = styled.button `
  ${ButtonReset};
  ${fonts.gotham.regular};

  position: relative;
  border-radius: 5000px;
  color: currentColor;
  font-size: 14px;
  height: 18px;
  width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 0 5px;
  cursor: help;
  text-transform: none;
  background-color: var(--infoBgColour);
  transform: translateY(-1.5px);
  z-index: 2;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
    outline-style: solid;
  }
`;
const Tooltip = styled.span `
  --shadowColour: rgba(0, 0, 0, 0.05);

  ${transition('opacity', 0.2)};

  position: absolute;
  color: var(--tooltipFgColour);
  background-color: var(--tooltipBgColour);
  top: 95%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  z-index: -1;
  width: 125px;
  padding: 6px 8px;
  box-shadow: -0.1px 1px 1.1px var(--shadowColour), -0.2px 2.7px 3px -0.8px var(--shadowColour),
    -0.4px 6.3px 7.1px -1.7px var(--shadowColour), -0.9px 15px 16.9px -2.5px var(--shadowColour);
  white-space: normal;
  pointer-events: none;
  border-radius: 4px;

  ${Info}:hover &,
  ${Info}:active &,
  ${Info}.focus-ring & {
    opacity: 1;
    z-index: 1;
    pointer-events: all;
  }
`;
const FormStyles = {
    ConfirmationMessage,
    Content,
    Description,
    Divider,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    Form,
    Label,
    Loader,
    LoaderText,
    Row,
    Section,
    SubmitWrapper,
    Title,
    Info,
    Tooltip,
};
export default FormStyles;
